/**
 * Spec №22 (D-CL4) — the autonomy dial, the desk maker control. Governs WHEN this
 * agent runs, never WHAT it may touch: the 11.2 / I9 walls hold at every setting, so
 * the wall-note below the dial is always shown (the dial and the wall are different
 * axes). Writes agent_config.autonomy via update_node; the engine enforces it at
 * run-start (enforceAutonomy). A fresh hire defaults to Off (David's ruling).
 */

import { useState, type CSSProperties } from "react";
import { mcpClient } from "../../../lib/mcp-client";
import type { AgentConfig } from "../../../lib/types";

type Level = "off" | "supervised" | "autonomous";

const LEVELS: { id: Level; label: string; blurb: string }[] = [
  { id: "off", label: "Off", blurb: "Drafts only. Every run parks for your approval — nothing lands without you." },
  { id: "supervised", label: "Supervised", blurb: "Runs when you press ▶ and watch the thread." },
  { id: "autonomous", label: "Autonomous", blurb: "Runs on materialisation / routine without you present — still every run a thread + Needs-You on escalation." },
];

const WHY: Record<Level, string> = {
  off: "Runs only as drafts — every run parks for you",
  supervised: "Runs when you press ▶ and watch",
  autonomous: "Runs on its own on materialisation / routine",
};

export function AutonomyDial({ agentId, cfg, wallReason, onChanged, compact }: {
  agentId: string;
  cfg: AgentConfig | null;
  wallReason: string;
  onChanged?: () => void;
  compact?: boolean;
}) {
  const [level, setLevel] = useState<Level>((cfg?.autonomy as Level) ?? "off");
  const [busy, setBusy] = useState<Level | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const setDial = async (next: Level) => {
    if (next === level || busy) return;
    setBusy(next);
    try {
      await mcpClient.updateNode({ id: agentId, agent_config: { ...(cfg ?? {} as AgentConfig), autonomy: next } });
      setLevel(next);
      onChanged?.();
    } catch {
      /* leave the dial where it was */
    } finally {
      setBusy(null);
    }
  };

  // №22.1 (D-CC7) — the compact dial that lives in the desk header STRIP: the seg3 +
  // a one-line why + a details toggle (the three paragraphs die into the toggle).
  if (compact) {
    return (
      <div style={{ textAlign: "right" }} data-testid="autonomy-dial">
        <div style={{ ...dial, borderColor: "var(--border-strong)" }} role="group" aria-label="autonomy">
          {LEVELS.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => setDial(l.id)}
              disabled={!!busy}
              style={{ ...dialBtn, padding: "6px 13px", ...(level === l.id ? (l.id === "autonomous" ? dialOnWarn : dialOn) : null) }}
              data-testid={`dial-${l.id}`}
              aria-pressed={level === l.id}
            >
              {busy === l.id ? "…" : l.label}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4 }}>
          {WHY[level]} · <b style={{ color: "var(--text-2)" }}>the wall never moves</b> ·{" "}
          <button type="button" onClick={() => setShowDetails((v) => !v)} style={detailsLink}>{showDetails ? "hide" : "details"}</button>
        </div>
        {showDetails && (
          <div style={{ ...wallNote, textAlign: "left", marginTop: 8, maxWidth: 340 }}>
            <b>The wall never moves.</b> {wallReason} At every setting, money it touches lands in the human I9 loop. The dial widens <i>timing</i>, never <i>authority</i>.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="card" data-testid="autonomy-dial">
      <div className="card__h"><span className="eyebrow">Does</span> Autonomy <span className="sub">how this desk runs · enforced at run-start (D-CL4)</span></div>
      <div className="card__b">
        <div style={dial} role="group" aria-label="autonomy">
          {LEVELS.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => setDial(l.id)}
              disabled={!!busy}
              style={{ ...dialBtn, ...(level === l.id ? (l.id === "autonomous" ? dialOnWarn : dialOn) : null) }}
              data-testid={`dial-${l.id}`}
              aria-pressed={level === l.id}
            >
              {busy === l.id ? "…" : l.label}
            </button>
          ))}
        </div>
        <div style={legend}>
          {LEVELS.map((l) => (
            <div key={l.id} style={{ opacity: level === l.id ? 1 : 0.6 }}>
              <b style={legendB}>{l.label}</b>{l.blurb}
            </div>
          ))}
        </div>
        <div style={wallNote}>
          <b>The wall never moves.</b> {wallReason} At every setting, money it touches lands in the human I9 loop.
          The dial widens <i>timing</i>, never <i>authority</i>.
        </div>
      </div>
    </div>
  );
}

const dial: CSSProperties = { display: "inline-flex", borderRadius: 10, overflow: "hidden", border: "0.5px solid var(--color-border-secondary)" };
const dialBtn: CSSProperties = { border: "none", borderRadius: 0, background: "transparent", padding: "8px 14px", fontSize: 12, fontWeight: 600, color: "var(--color-text-secondary)", cursor: "pointer", borderRight: "0.5px solid var(--color-border-tertiary)" };
const dialOn: CSSProperties = { background: "var(--color-text-primary)", color: "var(--color-background-primary)" };
const dialOnWarn: CSSProperties = { background: "var(--c-amber-400)", color: "#fff" };
const legend: CSSProperties = { display: "flex", gap: 18, marginTop: 12, flexWrap: "wrap" };
const legendB: CSSProperties = { color: "var(--color-text-primary)", display: "block", fontSize: 12 };
const wallNote: CSSProperties = { marginTop: 14, padding: "10px 13px", borderRadius: 10, background: "var(--color-background-danger)", border: "0.5px solid var(--color-border-danger)", fontSize: 12, lineHeight: 1.5, color: "var(--color-text-danger)" };
const detailsLink: CSSProperties = { border: "none", background: "none", padding: 0, cursor: "pointer", fontSize: 11, color: "var(--accent)", fontWeight: 600 };
